import { computed, action, observable, set } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { ChangeEvent } from 'react';
import {
    AutomationStepAction,
    IAutomationStep,
    IAutomationStepAction,
    IAutomationStepSession,
    IAutomationStepTrigger,
    TApprovalStatus,
    TAutomationStepBlockErrorMessageKeys
} from '../../types/automation.types';
import type { RootStore } from '~/stores/root.store';
import { prepClassForServer } from '../../utils/data.utils';
import { AutomationCombinedTypes } from '../../components/Automations/AutomationsEnums';
import { daysDifference } from '../../utils/date.utils';
import { shortTimezoneToFullTimezone } from '../../utils/timezone.utils';
import {
    IAutomationEmailSettings,
    IVendorEmail
} from '../../types/model.types';
import { AutomationStepActionTask } from './automations-step-actions/automation-step-action-task';
import { AutomationStepActionFile } from './automations-step-actions/automation-step-action-file';
import { AutomationStepActionEmail } from './automations-step-actions/automation-step-action-email';
import { AutomationStepActionFlow } from './automations-step-actions/automation-step-action-flow';
import {
    attachmentsCount,
    AUTOMATION_TRIGGERS_WITH_FILTERS,
    AUTOMATION_TRIGGER_EVENTS
} from '../../utils/automation.utils';
import { xor } from 'lodash';
import { AutomationStepActionPipeline } from '~/models/automations-steps/automations-step-actions/automation-step-action-pipeline';
import { IFlowsBetaUserType } from '~/models/user.model';

export class AutomationStep implements IAutomationStep {
    @observable _id?: string;
    @observable order: number;
    @observable approval_status: TApprovalStatus;
    @observable has_depended_step?: boolean;
    @observable workflow_step_action: AutomationStepAction;
    @observable workflow_step_trigger: IAutomationStepTrigger;
    @observable session?: IAutomationStepSession;

    @observable _tempId?: string;
    @observable _automationId?: string;
    rootStore: RootStore;

    constructor(
        automationStep: IAutomationStep,
        automationId: string,
        rootStore: RootStore
    ) {
        this.rootStore = rootStore;
        set(this, automationStep);
        this.workflow_step_trigger.trigger_filters ||= {};
        this._tempId = uuidv4();
        this._automationId = automationId;
        this.setStepAction(automationStep.workflow_step_action);
    }

    @action.bound
    setStepAction(stepAction: IAutomationStepAction) {
        const ActionType = (() => {
            switch (stepAction.type) {
                case 'email':
                    return AutomationStepActionEmail;
                case 'file':
                    return AutomationStepActionFile;
                case 'flow':
                    return AutomationStepActionFlow;
                case 'task':
                    return AutomationStepActionTask;
                case 'pipeline':
                    return AutomationStepActionPipeline;
                default:
                    return null;
            }
        })();
        this.workflow_step_action = new ActionType(stepAction, this.rootStore);
    }

    @computed
    get id() {
        return this._id || this._tempId;
    }

    @computed
    get type() {
        return this.workflow_step_action.type;
    }

    @computed
    get fileType() {
        return this.stepActionAsFile.file_type;
    }

    @computed
    get combinedType(): AutomationCombinedTypes {
        return this.fileType || this.type;
    }

    @computed
    get isTask() {
        return this.type === 'task';
    }

    @computed
    get isFlow() {
        return this.combinedType === 'flow';
    }

    @computed
    get isPipeline() {
        return this.combinedType === 'pipeline';
    }

    @computed
    get isCompleted() {
        return !!this.workflow_step_action.completed;
    }

    @computed
    get isDeclined() {
        return this.approval_status === 'declined';
    }

    @computed
    get isDone() {
        return this.isCompleted || this.isDeclined;
    }

    @computed
    get completedAt() {
        return this.workflow_step_action.completed_at;
    }

    @computed
    get declinedAt() {
        return this.workflow_step_action.declined_at;
    }

    @computed
    get doneAt() {
        return this.completedAt || this.declinedAt;
    }

    @computed
    get taskTitle() {
        return this.stepActionAsTask.task_title || '';
    }

    @computed
    get taskAssigneeId() {
        return this.stepActionAsTask.task_assignee_id;
    }

    @computed
    get genericEmail() {
        return this.stepActionAsFile.genericEmail;
    }

    @computed
    get emailSettings() {
        return this.stepActionAsFile.emailSettings;
    }

    @computed
    get fileTemplateId() {
        return this.stepActionAsFile.file_template_id;
    }

    @computed
    get email() {
        return this.stepActionAsFile.email;
    }

    get stage() {
        return this.stepActionAsPipelineStage.stage;
    }

    getSideBarConfig(userType: IFlowsBetaUserType, folderName?: string) {
        return this.workflow_step_action.getSideBarConfig(userType, folderName);
    }

    get AutomationBlockAction() {
        return this.workflow_step_action.AutomationBlockAction;
    }

    @computed
    get parentAutomation() {
        return this.rootStore.automationStore.automations[this._automationId];
    }

    @computed
    get calculatedTriggerDate() {
        const {
            parentAutomation,
            prevStep,
            isTriggerDependencyDone,
            isAfterAutomationApplied,
            workflow_step_trigger: {
                delay_temporal_amount,
                delay_temporal_unit
            }
        } = this;

        if (!isTriggerDependencyDone) {
            return null;
        }

        const triggerDate = new Date(
            isAfterAutomationApplied
                ? parentAutomation.applied_date
                : prevStep?.doneAt
        );

        switch (delay_temporal_unit) {
            case 'minutes':
                triggerDate.setMinutes(
                    triggerDate.getMinutes() + delay_temporal_amount
                );
                break;
            case 'hours':
                triggerDate.setHours(
                    triggerDate.getHours() + delay_temporal_amount
                );
                break;
            case 'days':
                triggerDate.setDate(
                    triggerDate.getDate() + delay_temporal_amount
                );
                break;
            case 'weeks':
                triggerDate.setDate(
                    triggerDate.getDate() + delay_temporal_amount * 7
                );
                break;
            default:
                break;
        }
        return triggerDate;
    }

    @computed
    get isTriggerDependencyDone() {
        const {
            parentAutomation,
            prevStep,
            isAfterPrevStepDone,
            isAfterAutomationApplied
        } = this;

        return (
            (isAfterAutomationApplied && !!parentAutomation.applied_date) ||
            (isAfterPrevStepDone && prevStep?.isDone)
        );
    }

    @computed
    get isCalculatedTriggerDateInThePast() {
        return (
            this.isImmediate ||
            Date.now() > this.calculatedTriggerDate?.getTime()
        );
    }

    @computed
    get isOverdue() {
        const {
            isDone,
            parentAutomation: { project_timezone },
            workflow_step_trigger: { trigger_date }
        } = this;

        return (
            !isDone &&
            project_timezone &&
            daysDifference(
                new Date().toLocaleString('en-us', {
                    timeZone: shortTimezoneToFullTimezone[project_timezone],
                    dateStyle: 'short'
                }),
                trigger_date
            ) > 0
        );
    }

    @computed
    get attachmentsCount() {
        return (
            this.stepActionAsFile.attachment_count ??
            (this.email ? attachmentsCount(this.email) : null)
        );
    }

    @computed
    get isApprovalRequired() {
        return this.approval_status !== 'not_required';
    }

    @computed
    get isAwaitingApproval() {
        return (
            this.approval_status === 'required' ||
            this.approval_status === 'pending'
        );
    }

    @computed
    get isImmediate() {
        return this.workflow_step_trigger.delay_temporal_amount === 0;
    }

    @computed
    get isExecutable() {
        return (
            !this.isDone &&
            !this.isAwaitingApproval &&
            this.isTriggerDependencyDone &&
            this.isCalculatedTriggerDateInThePast
        );
    }

    @computed
    get isOnlyStep() {
        return this.parentAutomation.workflow_steps.length === 1;
    }

    @computed
    get isFirstStep() {
        return !this.order;
    }

    @computed
    get isLastStep() {
        return this.order === this.parentAutomation.workflow_steps.length - 1;
    }

    @computed
    get prevStep(): AutomationStep {
        return !this.isFirstStep && !this.isOnlyStep
            ? this.parentAutomation.workflow_steps[this.order - 1]
            : null;
    }

    @computed
    get nextStep(): AutomationStep {
        return !this.isLastStep && !this.isOnlyStep
            ? this.parentAutomation.workflow_steps[this.order + 1]
            : null;
    }

    @computed
    get isAfterAutomationApplied() {
        return (
            this.workflow_step_trigger.trigger_type ===
            'after_workflow_execution'
        );
    }

    @computed
    get isAfterPrevStepDone() {
        return (
            this.workflow_step_trigger.trigger_type ===
                'after_step_execution' && !this.isDone
        );
    }

    // Represents file's meta-data, relevant to all file types
    @computed
    get fileTemplate() {
        return this.stepActionAsFile.fileTemplate;
    }

    @computed
    get flowTemplate() {
        return this.isFlow && this.stepActionAsFlow.flowTemplate;
    }

    @computed
    get title() {
        return this.workflow_step_action.title;
    }

    @computed
    get errorMessageKey(): TAutomationStepBlockErrorMessageKeys {
        return !this.isSessionSelectionValid
            ? 'missing_session'
            : this.workflow_step_action.errorMessageKey;
    }

    @computed
    get isValid() {
        return (
            this.workflow_step_action.isValid && this.isSessionSelectionValid
        );
    }

    @computed
    get isAllSessionsTriggerOn() {
        return this.triggerFilters?.is_all_sessions;
    }

    @computed
    private get stepActionAsTask() {
        return this.workflow_step_action as AutomationStepActionTask;
    }
    @computed
    private get stepActionAsPipelineStage() {
        return this.workflow_step_action as AutomationStepActionPipeline;
    }

    @computed
    private get stepActionAsFile() {
        return this.workflow_step_action as AutomationStepActionFile;
    }

    @computed
    private get stepActionAsFlow() {
        return this.workflow_step_action as AutomationStepActionFlow;
    }

    @computed
    get triggerFilters() {
        return this.workflow_step_trigger.trigger_filters;
    }

    @computed
    get validSessionConfigIds() {
        const {
            currCompany: { scheduleSessionConfigs }
        } = this.rootStore.companyStore;

        return this.triggerFilters.session_config_ids?.filter(configId =>
            scheduleSessionConfigs?.some(config => config._id === configId)
        );
    }
    @computed
    get validTemplateConfigIds() {
        const {
            currCompany: { flows }
        } = this.rootStore.companyStore;

        return this.triggerFilters.template_ids?.filter(tempId =>
            flows?.some(temp => temp._id === tempId)
        );
    }

    @computed
    get isScheduleTrigger() {
        return (
            this.workflow_step_trigger.trigger_type ===
                'after_session_scheduled' || this.isScheduleTimeTrigger
        );
    }

    @computed
    get isSmartFileTrigger() {
        return (
            this.workflow_step_trigger.trigger_type === 'after_flow_completed'
        );
    }

    @computed
    get isScheduleTimeTrigger() {
        return [
            'before_session_start',
            'after_session_start',
            'before_session_end',
            'after_session_end'
        ].includes(this.workflow_step_trigger.trigger_type);
    }

    @computed
    get isSessionSelectionValid() {
        return (
            !this.isScheduleTrigger ||
            this.triggerFilters?.is_all_sessions ||
            this.validSessionConfigIds?.length
        );
    }
    @computed
    get isTemplateSelectionValid() {
        return (
            !this.isSmartFileTrigger ||
            this.triggerFilters?.is_all_templates ||
            this.validTemplateConfigIds?.length
        );
    }

    @action.bound
    private setTriggerDefaultValues(
        stepTrigger: Partial<IAutomationStepTrigger>
    ) {
        if (!stepTrigger.trigger_type) {
            return;
        }
        if (
            AUTOMATION_TRIGGER_EVENTS.find(
                curr => curr.value === stepTrigger.trigger_type
            )?.filters?.includes('session_configs')
        ) {
            this.toggleAllSessionConfigs({ overrideValue: true });
        }
        if (
            AUTOMATION_TRIGGER_EVENTS.find(
                curr => curr.value === stepTrigger.trigger_type
            )?.filters?.includes('templates')
        ) {
            this.toggleAllTemplatesConfigs({ overrideValue: true });
        }
    }

    @action.bound
    updateStepTrigger(stepTrigger: Partial<IAutomationStepTrigger>) {
        set(this.workflow_step_trigger, stepTrigger);
        this.verifyTriggerType();
        this.setTriggerDefaultValues(stepTrigger);
    }

    @action.bound
    toggleSessionConfig(sessionId: string) {
        const { currCompany } = this.rootStore.companyStore;

        this.triggerFilters.session_config_ids = xor(
            this.validSessionConfigIds || [],
            [sessionId]
        );
        this.triggerFilters.is_all_sessions =
            this.triggerFilters.session_config_ids.length ===
            currCompany.scheduleSessionConfigs.length;
    }
    @action.bound
    toggleTemplatesConfig(templateId: string) {
        const { currCompany } = this.rootStore.companyStore;

        this.triggerFilters.template_ids = xor(
            this.validTemplateConfigIds || [],
            [templateId]
        );
        this.triggerFilters.is_all_templates =
            this.triggerFilters.template_ids.length ===
            currCompany.flows.length;
    }

    @action.bound
    toggleAllSessionConfigs(props?: { overrideValue?: boolean }) {
        const {
            currCompany: { scheduleSessionConfigs }
        } = this.rootStore.companyStore;

        const value = !this.triggerFilters.is_all_sessions;

        this.triggerFilters.is_all_sessions =
            typeof props?.overrideValue === 'boolean'
                ? props?.overrideValue
                : value;

        this.triggerFilters.session_config_ids = this.triggerFilters
            .is_all_sessions
            ? scheduleSessionConfigs.map(curr => curr._id)
            : [];
    }
    @action.bound
    toggleAllTemplatesConfigs(props?: { overrideValue?: boolean }) {
        const {
            currCompany: { flows }
        } = this.rootStore.companyStore;

        const value = !this.triggerFilters.is_all_templates;

        this.triggerFilters.is_all_templates =
            typeof props?.overrideValue === 'boolean'
                ? props?.overrideValue
                : value;

        this.triggerFilters.template_ids = this.triggerFilters.is_all_templates
            ? flows.map(curr => curr._id)
            : [];
    }

    @action.bound
    setGenericEmail(email: IVendorEmail) {
        this.stepActionAsFile.setGenericEmail(email);
    }

    @action.bound
    setEmailSettings(emailSettings: IAutomationEmailSettings) {
        this.stepActionAsFile.setEmailSettings(emailSettings);
    }

    @action.bound
    toggleApprovalMethod() {
        this.approval_status = this.isApprovalRequired
            ? 'not_required'
            : 'required';
        this.verifyTriggerType();
    }

    @action.bound
    updateTaskTitle(e: ChangeEvent<HTMLInputElement>) {
        this.stepActionAsTask.updateTaskTitle(e.target.value);
    }

    @action.bound
    updateTaskAssigneeId(assigneeId: string) {
        this.stepActionAsTask.updateTaskAssigneeId(assigneeId);
    }

    @action.bound
    updateSelectedStage(stage) {
        this.stepActionAsPipelineStage.updatePipelineStage(stage);
    }

    @action.bound
    selectFileTemplate(id: string) {
        this.stepActionAsFile.selectFileTemplate(id);
    }

    @action.bound
    selectEmailTemplate(id: string) {
        this.stepActionAsFile.selectEmailTemplate(id);
    }

    @action.bound
    updateOrder(order: number) {
        this.order = order;
        this.verifyTriggerType();
    }

    @action.bound
    verifyTriggerType() {
        if (!this.order && this.isAfterPrevStepDone) {
            this.workflow_step_trigger.trigger_type =
                'after_workflow_execution';
        }
    }

    @action.bound
    initEmailSettings() {
        // make sure default value exists
        const hasLogoOrIcon = !!this.flowTemplate?.logoForEmail;
        const isLogoEnabled =
            this.emailSettings?.is_logo_enabled ?? hasLogoOrIcon;

        this.setEmailSettings({
            ...this.emailSettings,
            is_logo_enabled: isLogoEnabled
        });
    }

    @action.bound
    loadFlowConfiguration() {
        // make sure default value exists
        const hasLogoOrIcon = !!this.flowTemplate?.logoForEmail;
        const isLogoEnabled =
            this.flowTemplate?.configuration?.is_logo_enabled ?? hasLogoOrIcon;

        this.selectEmailTemplate(
            this.flowTemplate?.configuration?.default_email_template_id
        );
        this.setEmailSettings({
            is_logo_enabled: isLogoEnabled,
            cta_button_text: this.flowTemplate?.configuration?.cta_button_text
        });
    }

    getDataForServer(): IAutomationStep {
        const dataForServer = prepClassForServer(this, [
            '_tempId',
            '_automationId',
            'workflow_step_action'
        ]);
        dataForServer.workflow_step_action =
            this.workflow_step_action.getDataForServer();

        if (
            !AUTOMATION_TRIGGERS_WITH_FILTERS.includes(
                this.workflow_step_trigger.trigger_type
            )
        ) {
            dataForServer.workflow_step_trigger.trigger_filters = null;
        } else if (this.validSessionConfigIds) {
            dataForServer.workflow_step_trigger.trigger_filters.session_config_ids =
                this.validSessionConfigIds;
        } else if (this.validTemplateConfigIds) {
            dataForServer.workflow_step_trigger.trigger_filters.template_ids =
                this.validTemplateConfigIds;
        }

        return dataForServer;
    }
}
